/**
 * ScrollspyModal — 긴 폼을 좌측 섹션 nav + 우측 스크롤 폼으로 보여주는 일반 모달.
 *
 * 사용:
 *   <ScrollspyModal
 *     open
 *     onClose={...}
 *     title="배합비 편집"
 *     sections={[
 *       { id: "basic", label: "기본 정보", content: <>...</> },
 *       { id: "cement", label: "시멘트 · 혼화재", content: <>...</> },
 *     ]}
 *     footer={<><button>취소</button><button>저장</button></>}
 *   />
 *
 * 동작:
 *   - 좌측 nav 클릭 → 우측 컨테이너에서 해당 섹션으로 smooth scroll
 *   - 사용자가 직접 스크롤하면 IntersectionObserver로 가장 위쪽에 보이는 섹션이 active
 *   - 메뉴 클릭으로 인한 스크롤 동안에는 observer 일시 disable (active 깜빡임 방지)
 *   - prefers-reduced-motion 환경: smooth → instant
 *   - ESC / backdrop 클릭(옵션) 닫기
 *
 * 디자인:
 *   - 탭 형식인 SettingsModal과 시각적으로 구분되도록 dot indicator + 가는 좌측 line.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

export interface ScrollspySection {
  id: string;
  label: string;
  content: ReactNode;
}

interface ScrollspyModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  sections: ScrollspySection[];
  footer?: ReactNode;
  /** 모달 본체 폭 (default 1080 — SettingsModal과 동일) */
  width?: number;
  /** 모달 본체 높이 (default min(720px, 90vh) — SettingsModal과 동일) */
  height?: string;
  /** backdrop 클릭으로 닫기 허용 (default true) */
  closeOnBackdrop?: boolean;
  /** ESC로 닫기 허용 (default true) */
  closeOnEscape?: boolean;
  /** 좌측 nav 맨 아래에 추가 렌더링할 노드 (예: 별도 액션 버튼) */
  bottomNavExtra?: ReactNode;
  /** 우측 고정 패널 (예: 실시간 preview). 지정 시 본문이 [nav · content · rightPane] 3컬럼 */
  rightPane?: ReactNode;
  /** rightPane 폭 (default 460px) */
  rightPaneWidth?: number;
}

export function ScrollspyModal({
  open,
  onClose,
  title,
  sections,
  footer,
  width = 1080,
  height = "min(720px, 90vh)",
  closeOnBackdrop = true,
  closeOnEscape = true,
  bottomNavExtra,
  rightPane,
  rightPaneWidth = 460,
}: ScrollspyModalProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");
  /** preview dock 고정 핀 — true면 hover 없이도 패널 항상 열림 */
  const [previewPinned, setPreviewPinned] = useState(false);
  /** preview hover state (JS 관리) — tab/panel 사이 이동 시 leave에 80ms 디바운스 */
  const [previewHover, setPreviewHover] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  // 메뉴 클릭으로 발생한 프로그램적 스크롤은 observer 무시 (active 깜빡임 방지)
  const programmaticRef = useRef(false);

  const setHoverDeferred = (value: boolean) => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (value) {
      setPreviewHover(true);
    } else {
      hoverTimerRef.current = window.setTimeout(() => setPreviewHover(false), 80);
    }
  };

  // sections 변경 시 active 초기화
  useEffect(() => {
    if (!open) return;
    setActiveId(sections[0]?.id ?? "");
  }, [open, sections]);

  // ESC 닫기
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, closeOnEscape]);

  // IntersectionObserver — 우측 컨테이너 기준으로 보이는 섹션 추적
  useEffect(() => {
    if (!open) return;
    const root = scrollRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (programmaticRef.current) return;
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        // 가장 위쪽(top이 작은) 섹션을 active로
        const top = visible.reduce((best, cur) =>
          cur.boundingClientRect.top < best.boundingClientRect.top ? cur : best,
        );
        const id = (top.target as HTMLElement).dataset.sectionId;
        if (id) setActiveId(id);
      },
      {
        root,
        // 섹션 상단이 컨테이너 상단에서 약 30% 지점에 들어오면 active
        rootMargin: "0px 0px -65% 0px",
        threshold: 0,
      },
    );

    sections.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [open, sections]);

  function jumpTo(id: string) {
    const el = sectionRefs.current[id];
    const root = scrollRef.current;
    if (!el || !root) return;

    setActiveId(id);
    programmaticRef.current = true;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });

    // 스크롤 완료 후 observer 재활성화. scrollend 미지원 브라우저는 timeout fallback.
    if ("onscrollend" in root) {
      const reEnable = () => {
        programmaticRef.current = false;
      };
      root.addEventListener("scrollend", reEnable, { once: true });
    } else {
      window.setTimeout(() => {
        programmaticRef.current = false;
      }, 600);
    }
  }

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-scrollspy"
        style={{ width, height }}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-form-head">
          <h3>{title}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>
        <div className="mss-body">
          <nav className="mss-nav" aria-label="섹션 메뉴">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`mss-nav-item ${activeId === s.id ? "is-active" : ""}`}
                onClick={() => jumpTo(s.id)}
                aria-current={activeId === s.id ? "true" : undefined}
              >
                <span className="mss-nav-dot" aria-hidden="true" />
                <span className="mss-nav-label">{s.label}</span>
              </button>
            ))}
            {bottomNavExtra && <div className="mss-nav-extra">{bottomNavExtra}</div>}
          </nav>
          <div className="mss-content" ref={scrollRef}>
            {sections.map((s) => (
              <section
                key={s.id}
                data-section-id={s.id}
                ref={(el) => {
                  sectionRefs.current[s.id] = el;
                }}
                className="mss-section"
              >
                <h2 className="mss-section-title">{s.label}</h2>
                {s.content}
              </section>
            ))}
          </div>
        </div>
        {rightPane && (
          <div
            className={`mss-preview-dock ${previewPinned ? "is-pinned" : ""} ${previewHover ? "is-hover" : ""}`}
            aria-label="미리보기 도크"
          >
            <div
              className="mss-preview-panel"
              style={{ width: rightPaneWidth }}
              onMouseEnter={() => setHoverDeferred(true)}
              onMouseLeave={() => setHoverDeferred(false)}
            >
              {rightPane}
            </div>
            <div
              className="mss-preview-tab"
              onMouseEnter={() => setHoverDeferred(true)}
              onMouseLeave={() => setHoverDeferred(false)}
            >
              <div className="mss-preview-tab-label" aria-hidden="true">
                <span>미</span><span>리</span><span>보</span><span>기</span>
              </div>
              <button
                type="button"
                className={`mss-preview-pin ${previewPinned ? "is-pinned" : ""}`}
                onClick={() => {
                  setPreviewPinned((p) => {
                    const next = !p;
                    // 고정 해제 시 즉시 hover state 도 false → 패널 바로 닫힘
                    // (mouse 가 dock 위에 있어도 무시. 다음 mouseenter 부터 다시 hover 인식)
                    if (!next) {
                      if (hoverTimerRef.current != null) {
                        window.clearTimeout(hoverTimerRef.current);
                        hoverTimerRef.current = null;
                      }
                      setPreviewHover(false);
                    }
                    return next;
                  });
                }}
                aria-pressed={previewPinned ? "true" : "false"}
                title={previewPinned ? "고정 해제" : "미리보기 고정"}
                aria-label={previewPinned ? "고정 해제" : "미리보기 고정"}
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="12" y1="17" x2="12" y2="22" />
                  <path d="M5 17h14l-1.5-3.5a2 2 0 0 1-.18-.83V7h.68a1.5 1.5 0 0 0 0-3H6a1.5 1.5 0 0 0 0 3h.68v5.67a2 2 0 0 1-.18.83Z" />
                </svg>
              </button>
            </div>
          </div>
        )}
        {footer && <div className="modal-form-actions">{footer}</div>}
      </div>
    </div>
  );
}
